import React, { useEffect, useMemo, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Accordion, Card } from 'react-bootstrap'
import PropTypes from 'prop-types'
import moment from 'moment'
import cn from 'classnames'

import TipDialogueBox from 'components/Shared/TipDialogueBox'
import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'
import InfoBox from 'components/Shared/InfoBox'
import { numberWithThousandsSeparators, getAdSuggestions, getMediaDimensions } from 'utils/helpers'
import AdsSummaryItem from './AdsSummaryItem'
import { initStateObject, objectReducer, UPDATE, FAIL } from 'reducers/default'
import { fetchPageInfos } from 'api/integrations'
import campaignSetupApi from 'domain/campaignSetup/api'
import { AssetClass, Platform } from 'api/models'
import {
  getTotalDisabledVariations,
  hasEnabledVariations,
} from 'components/Recommendations/Campaign/Context/disabledVariations'
import { getActivePlatformsForSetup } from 'domain/adPreview/fromSetup'
import { PlacementPlatform } from 'domain/adPreview/placementGroups'
import { deriveCampaignTypeFromSetup, toApiCampaignType } from '../Goals/campaignType'
import { countNaturalVariations } from 'domain/adCount/googleAdCount'
import PortalModal from 'components/Shared/PortalModal'
import {
  useCampaignSetupDispatch,
  useCampaignSetup,
} from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import Notice from 'components/Shared/Notice'
import AgeRange from 'components/Recommendations/EventCampaigns/Steps/Setup/Summary/advancedSettings/AgeRange'
import CustomToggle from 'components/Shared/bootstrapComponentHelpers/CustomToggle'
import CampaignOptimisation from './advancedSettings/CampaignOptimisation'
import DsaFields from './advancedSettings/DsaFields/DsaFields'
import ShareSummaryItem from './ShareSummaryItem'

const SummaryItem = ({ title, value, children, className }) => {
  return (
    <div className={`card card-no-padding-mobile rounded flex-row align-items-center ${className}`}>
      <div className="summary_item summary_item_icon">
        <i className="icon-campaign" />
      </div>
      <p className="summary_item summary_item_content">
        <strong>{value}</strong>
        <span>{title}</span>
      </p>
      {children}
    </div>
  )
}

SummaryItem.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
}

const Summary = ({ campaigns, event, onBack, boost, setShowCancelSetupModal }) => {
  const { t } = useTranslation()
  const [adSuggestionsLoading, setAdSuggestionsLoading] = useState(true)
  const [totalAds, setTotalAds] = useState({ meta: 0, google: 0 })
  const [fbPageInfo, setFbPageInfo] = useReducer(objectReducer, initStateObject)
  const [budgetPreview, setBudgetPreview] = useReducer(objectReducer, initStateObject)
  const [advancedSettingsActiveKey, setAdvancedSettingsActiveKey] = useState(null)
  const { setup, canAnonEdit, canAnonBoost, anonScopes } = useCampaignSetup()
  const isAnonUser = anonScopes !== null
  const {
    isStepValid,
    checkDisabledVariationsIntegrity,
    getAdAccount,
    getAgeTargeting,
    updateAgeTargeting,
    isAgeRangeDisabled,
    isGeneratingValid,
  } = useCampaignSetupDispatch()
  const [disabledVariationsWarningModalOpen, setDisabledVariationsWarningModalOpen] =
    useState(false)

  const activePlatforms = useMemo(() => getActivePlatformsForSetup(setup), [setup])
  const includesMeta = activePlatforms.includes(PlacementPlatform.META)
  const includesGoogle = activePlatforms.includes(PlacementPlatform.GOOGLE)

  const getRuntime = () => {
    return `${new moment(setup.start_date).format('DD.MM.YYYY')} - ${moment(setup.end_date).format('DD.MM.YYYY')}`
  }

  useEffect(() => {
    const getInfo = async () => {
      const meta_profile = setup.integration_details.find(
        (integration_detail) =>
          integration_detail.asset_class === AssetClass.profile &&
          integration_detail.platform === Platform.facebook,
      )
      await fetchPageInfos(setFbPageInfo, meta_profile?.asset_id)
    }

    getInfo()
  }, [setup.integration_details])

  useEffect(() => {
    const constructCreative = async () => {
      if (setup.creatives?.length) {
        setAdSuggestionsLoading(true)
        const newCreatives = await Promise.all(
          setup.creatives.map(async function (creative) {
            const media = await Promise.all(
              creative.media.map(async function (media) {
                const mediaDimensions = await getMediaDimensions(media)
                return {
                  ...media,
                  height: mediaDimensions.height,
                  width: mediaDimensions.width,
                  ratio: mediaDimensions.height / mediaDimensions.width,
                }
              }),
            )

            // Also process placement_media to calculate dimensions and ratio for grouped reels
            const placement_media = creative.placement_media
              ? await Promise.all(
                  creative.placement_media.map(async function (placementMedia) {
                    const mediaDimensions = await getMediaDimensions(placementMedia)
                    return {
                      ...placementMedia,
                      height: mediaDimensions.height,
                      width: mediaDimensions.width,
                      ratio: mediaDimensions.height / mediaDimensions.width,
                    }
                  }),
                )
              : []

            return {
              ...creative,
              media,
              placement_media,
            }
          }),
        ).catch((error) => {
          console.error('Error constructing creatives:', error)
          return setup.creatives
        })

        const platforms = getActivePlatformsForSetup(setup)
        const includesMeta = platforms.includes(PlacementPlatform.META)
        const includesGoogle = platforms.includes(PlacementPlatform.GOOGLE)

        let metaTotal = 0
        if (includesMeta) {
          const [regular, reels] = getAdSuggestions(newCreatives, [fbPageInfo], t)
          const totalDisabledVariations = getTotalDisabledVariations(setup.creatives, reels)
          metaTotal = regular.length + reels.length - totalDisabledVariations
        }

        let googleTotal = 0
        if (includesGoogle) {
          const campaignType = toApiCampaignType(deriveCampaignTypeFromSetup(setup))
          googleTotal = newCreatives.reduce(
            (sum, creative) => sum + countNaturalVariations(creative, campaignType),
            0,
          )
        }

        setTotalAds({ meta: metaTotal, google: googleTotal })
      }
      setAdSuggestionsLoading(false)
    }
    constructCreative()
  }, [setup.creatives, fbPageInfo, t, setup])

  const totalAdsValue = `${totalAds.meta + totalAds.google}`

  const clientCurrency = localStorage.getItem('userCurrency')
  const campaignCurrency = useMemo(() => getAdAccount()?.currency || '', [getAdAccount])
  const budgetInAdAccountCurrency = budgetPreview.content.budget_in_ad_account_currency
  const budgetInAdAccountCurrencyPending = !budgetPreview.error && budgetInAdAccountCurrency == null

  useEffect(() => {
    if (!event.id) return

    let cancelled = false
    const getBudgetPreview = async () => {
      const { data, error } = await campaignSetupApi.previewAsync(event.id)
      if (cancelled) return
      if (error) {
        setBudgetPreview({ type: FAIL })
        return
      }
      setBudgetPreview({ type: UPDATE, payload: data })
    }

    getBudgetPreview()

    return () => {
      cancelled = true
    }
  }, [event.id, setup.total_budget])

  useEffect(() => {
    const mobileNav = document.getElementById('mobile-navbar')
    if (mobileNav?.style) {
      mobileNav.style.display = 'none'
    }

    return () => {
      const mobileNav = document.getElementById('mobile-navbar')
      if (mobileNav?.style) {
        mobileNav.style.display = 'block'
      }
    }
  }, [])

  return (
    <>
      <div id="summary">
        <TipDialogueBox
          title={t('Recommendations.summary.title')}
          text={t('Recommendations.summaryTextTBD')}
        />

        <div className="accordion mt-4">
          <SummaryItem
            title={t('Recommendations.summary.budget')}
            value={
              budgetPreview.error
                ? t('common.error')
                : budgetInAdAccountCurrencyPending
                  ? t('common.loading')
                  : `${numberWithThousandsSeparators(budgetInAdAccountCurrency)} ${campaignCurrency}`
            }
          >
            <div className="ml-3 ml-md-4 d-flex align-items-center">
              <Notice
                isVisible={
                  !budgetPreview.error &&
                  !budgetInAdAccountCurrencyPending &&
                  setup.total_budget !== budgetInAdAccountCurrency &&
                  clientCurrency !== campaignCurrency
                }
                isWarning={false}
                className="summary_notice"
                message={t('Recommendations.summary.budgetCurrencyNotice', {
                  budget: numberWithThousandsSeparators(setup.total_budget),
                  baseCurrency: clientCurrency,
                  convertedBudget: numberWithThousandsSeparators(budgetInAdAccountCurrency),
                  targetCurrency: campaignCurrency,
                })}
              />
            </div>
          </SummaryItem>

          <SummaryItem
            title={t('Recommendations.summary.totalTasteClusters')}
            value={`${campaigns.length}`}
          />

          <SummaryItem
            title={t('Recommendations.summary.runtime')}
            value={getRuntime()}
            className="d-block d-md-flex"
          >
            <Notice
              className="summary_notice ml-sm-1 mr-1 mr-md-0 ml-md-4 my-2 my-md-0"
              message={t('Recommendations.campaignRuntime.beyondEventDateAlert')}
              isVisible={moment(setup.end_date).isAfter(event.date)}
            />
          </SummaryItem>

          <AdsSummaryItem
            title={t('Recommendations.summary.totalAds')}
            value={totalAdsValue}
            loading={adSuggestionsLoading}
            pageInfo={fbPageInfo.content}
          />

          {!isAnonUser && <ShareSummaryItem eventId={event.id} />}
        </div>

        <Accordion className="my-3 summary_advanced-settings">
          <Card className="m-0 overflow-visible">
            <CustomToggle
              eventKey="0"
              onClick={() => setAdvancedSettingsActiveKey((ak) => (ak === '0' ? null : '0'))}
            >
              <div className="campaign-header_toggle__title">
                {t('Recommendations.summary.advancedSettings.title')}

                <div className="align-self-center ml-auto">
                  <div
                    className={`accordion-arrow ${advancedSettingsActiveKey === '0' ? 'open' : ''}`}
                  />
                </div>
              </div>
            </CustomToggle>

            <Accordion.Collapse
              as={Card.Body}
              className={cn('flex-column', { 'd-flex': advancedSettingsActiveKey === '0' })}
              eventKey="0"
            >
              <div className="wrapper">
                <AgeRange
                  value={getAgeTargeting()}
                  onChange={(value) => {
                    updateAgeTargeting(value.min, value.max)
                  }}
                  disabled={isAgeRangeDisabled(event) || !canAnonEdit}
                />

                {includesMeta && (
                  <>
                    <CampaignOptimisation
                      event={event}
                      disabled={!canAnonEdit}
                      bothSelected={includesGoogle}
                    />

                    <DsaFields disabled={!canAnonEdit}  bothSelected={includesGoogle}/>
                  </>
                )}
              </div>
            </Accordion.Collapse>
          </Card>
        </Accordion>

        <div
          id="setup-btn-container"
          className="recommendation-setup-action-buttons d-flex justify-content-between align-items-center"
        >
          <div className="d-flex justify-content-between w-100">
            <TransparentBgButton
              className="bt"
              onClick={() => onBack()}
              text={t('Recommendations.btnBack')}
              fullWidth={false}
              color="navy"
            />
            <div className="d-flex gap-3 ml-auto">
              {!isAnonUser && (
                <InfoBox
                  body={t('Recommendations.cancelSetupGeneratingTooltip')}
                  withAnchor={false}
                  enabled={!isGeneratingValid()}
                >
                  <TransparentBgButton
                    text={t('Recommendations.btnCancelSetup')}
                    onClick={() => setShowCancelSetupModal(true)}
                    color="navy"
                    disabled={!isGeneratingValid()}
                    disabledTitle=""
                  />
                </InfoBox>
              )}
              {canAnonBoost && (
                <SecondaryButton
                  disabled={!isStepValid(4) || !hasEnabledVariations(setup.creatives)}
                  className="bt"
                  onClick={() => {
                    if (!checkDisabledVariationsIntegrity()) {
                      setDisabledVariationsWarningModalOpen(true)
                      return
                    }
                    boost()
                  }}
                  text={t('Recommendations.summary.publish')}
                  fullWidth={false}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <PortalModal
        isOpen={disabledVariationsWarningModalOpen}
        onClickOutside={() => setDisabledVariationsWarningModalOpen(false)}
      >
        <div className="content compact text-center" style={{ maxWidth: '420px' }}>
          <h1 className="autofill_warning-header">
            {t('Recommendations.summary.adVariationsRemainDisabled')}
          </h1>
          <p className="autofill_warning-msg">
            {t('Recommendations.summary.adVariationsRemainDisabledDescription')}
          </p>
          <div className="footer">
            <TransparentBgButton
              text={t('common.cancel')}
              onClick={() => {
                setDisabledVariationsWarningModalOpen(false)
              }}
              color="navy"
            />
            <SecondaryButton
              color="orange"
              text={t('common.proceed')}
              onClick={() => {
                setDisabledVariationsWarningModalOpen(false)
                boost()
              }}
              type="button"
              disabledTitle=""
            />
          </div>
        </div>
      </PortalModal>
    </>
  )
}

Summary.propTypes = {
  campaigns: PropTypes.array.isRequired,
  event: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  boost: PropTypes.func.isRequired,
  setShowCancelSetupModal: PropTypes.func.isRequired,
  totalBudget: PropTypes.number,
  creatives: PropTypes.array,
  runtimeStartDate: PropTypes.string,
  runtimeEndDate: PropTypes.string,
  integrationDetails: PropTypes.arrayOf(Object),
  onDisabledVariationsChange: PropTypes.func,
}

export default Summary
